import React from 'react';
import { Button, Popover, Typography } from 'antd';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SupplierContact, normalizeSupplierContacts } from '../../utils/supplierContacts';
import './SupplierContactCell.css';

const { Text } = Typography;

interface SupplierContactCellProps {
  contacts?: SupplierContact[] | null;
  contactPerson?: string | null;
  phone?: string | null;
  email?: string | null;
  emptyValue?: string;
}

const SupplierContactCell: React.FC<SupplierContactCellProps> = ({
  contacts,
  contactPerson,
  phone,
  email,
  emptyValue = '—'
}) => {
  const { t } = useTranslation('common');
  const normalizedContacts = normalizeSupplierContacts(contacts, contactPerson, phone, email);
  const primaryContact = normalizedContacts[0] || null;
  const extraContactsCount = Math.max(normalizedContacts.length - 1, 0);
  const contactRows = normalizedContacts.map((contact, index) => ({
    ...contact,
    key: `${contact.name || 'contact'}-${contact.phone || contact.email || 'detail'}-${index}`,
    isPrimaryRow: contact === primaryContact
  }));

  if (!primaryContact?.name && !primaryContact?.phone && !primaryContact?.email) {
    return <Text type="secondary">{emptyValue}</Text>;
  }

  const contactPopover = (
    <div className="supplier-contact-popover">
      <Text strong className="supplier-contact-popover__title">
        {t('contacts.title')}
      </Text>
      <div className="supplier-contact-popover__list">
        {contactRows.map((contact) => (
          <div key={contact.key} className="supplier-contact-popover__item">
            <div className="supplier-contact-popover__header">
              <Text strong>{contact.name || emptyValue}</Text>
              {contact.isPrimaryRow && (
                <span className="supplier-contact-popover__primary">
                  {t('contacts.primary')}
                </span>
              )}
            </div>
            {contact.phone && (
              <span className="supplier-contact-popover__detail">
                <PhoneOutlined />
                {contact.phone}
              </span>
            )}
            {contact.email && (
              <span className="supplier-contact-popover__detail">
                <MailOutlined />
                {contact.email}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <span className="supplier-contact-cell">
      <span className="supplier-contact-summary">
        <span className="supplier-contact-lines">
          {primaryContact?.name ? (
            <span className="supplier-contact-primary">{primaryContact.name}</span>
          ) : (
            <Text type="secondary">{emptyValue}</Text>
          )}
        </span>
        {extraContactsCount > 0 && (
          <Popover
            content={contactPopover}
            placement="rightTop"
            trigger="click"
            overlayClassName="supplier-contact-popover-overlay"
          >
            <Button
              type="text"
              size="small"
              className="supplier-contact-extra"
              aria-label={t('contacts.showAdditional')}
            >
              +{extraContactsCount}
            </Button>
          </Popover>
        )}
      </span>
      <span className="supplier-contact-details">
        {primaryContact?.phone && (
          <span className="supplier-contact-secondary">
            <PhoneOutlined />
            {primaryContact.phone}
          </span>
        )}
        {primaryContact?.email && (
          <span className="supplier-contact-secondary">
            <MailOutlined />
            {primaryContact.email}
          </span>
        )}
        {!primaryContact?.phone && !primaryContact?.email && (
          <Text type="secondary">{emptyValue}</Text>
        )}
      </span>
    </span>
  );
};

export default SupplierContactCell;
